"use client";

import type { StorageUsage } from "@/features/chat/Types/conversations";

import { useTranslation } from "react-i18next";
import { AlertTriangle, HardDrive } from "lucide-react";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface StorageUsageIndicatorProps {
  storageUsage: StorageUsage;
  className?: string;
}

export function StorageUsageIndicator({ storageUsage, className }: StorageUsageIndicatorProps) {
  const { t } = useTranslation(["chat"]);

  const percentage = storageUsage.usage_percentage;
  const isWarning = percentage > 80 && percentage <= 95;
  const isCritical = percentage > 95;

  // Only show if usage is above 80%
  if (percentage <= 80) return null;

  const getProgressColor = () => {
    if (isCritical) return "bg-red-500";
    if (isWarning) return "bg-yellow-500";

    return "bg-blue-500";
  };

  const getIcon = () => {
    if (isCritical) return <AlertTriangle className="h-3 w-3 text-red-500" />;
    if (isWarning) return <AlertTriangle className="h-3 w-3 text-yellow-500" />;

    return <HardDrive className="h-3 w-3 text-blue-500" />;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex items-center gap-1 text-xs", className)}>
            {getIcon()}
            <span className="text-muted-foreground">
              {storageUsage.total_size_formatted} / {storageUsage.max_size_formatted}
            </span>
            <div className="bg-muted/30 h-1 w-8 overflow-hidden rounded-full">
              <div
                className={cn("h-full transition-all duration-300", getProgressColor())}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-xs">
            <div className="mb-1 font-semibold">{t("chat:storage.title")}</div>
            <div className="space-y-1">
              <div>
                {t("chat:storage.usage", {
                  used: storageUsage.total_size_formatted,
                  total: storageUsage.max_size_formatted,
                })}
              </div>
              <div>
                {t("chat:storage.remaining", { remaining: storageUsage.remaining_size_formatted })}
              </div>
              <div>{t("chat:storage.files", { count: storageUsage.files_count })}</div>
              {!storageUsage.can_upload && (
                <div className="font-medium text-red-500">{t("chat:storage.full")}</div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
